import {
  ADVANCE_METHODS,
  EXPENSE_FORM_RULES,
  expenseFormKind,
  mileageTotalCents,
  paymentOf,
  paymentWayOf,
  type ExpenseFormKind,
  type PaymentWay,
} from '@console/shared-kernel';
import type { components } from '@/lib/api-types-expenses';
import type { ReceiptPrefill } from '@/lib/prefill-receipt';
import { parseAmountInput } from '../money';

type Schemas = components['schemas'];
export type Expense = Schemas['Expense'];
export type ExpenseInput = Schemas['ExpenseInput'];
export type DocumentType = Schemas['DocumentType'];
export type DocumentStatus = Schemas['DocumentStatus'];
export type TaxLine = Schemas['TaxLine'];
export type TaxType = TaxLine['type'];
export type TaxMode = TaxLine['mode'];

export const DOCUMENT_TYPES: readonly DocumentType[] = [
  'receipt',
  'receipt-with-tax-code',
  'invoice',
  'simplified-invoice',
  'ticket',
  'other',
];
export type AdvanceMethod = (typeof ADVANCE_METHODS)[number];
export const TAX_TYPES: readonly TaxType[] = ['vat', 'city-tax', 'stamp-duty', 'other'];
export const TAX_MODES: readonly TaxMode[] = ['rate', 'fixed', 'exempt'];
export const VAT_NATURES = ['N1', 'N2', 'N3', 'N4', 'N5', 'N6', 'N7'] as const;

/**
 * One tax line as typed: amounts are the text in the field («44,09»), turned into API amounts
 * only when checked or sent.
 */
export interface TaxLineDraft {
  readonly key: string;
  readonly type: TaxType;
  readonly mode: TaxMode;
  readonly rate: string;
  readonly nature: string;
  readonly base: string;
  readonly tax: string;
  readonly rateUnclear: boolean;
}

/** The capture form as typed. Amounts are field text; the date is the date input's YYYY-MM-DD. */
export interface ExpenseForm {
  readonly date: string;
  readonly total: string;
  readonly supplierName: string;
  readonly supplierVat: string;
  readonly documentNumber: string;
  readonly category: string;
  readonly documentType: DocumentType | '';
  readonly nonTaxable: string;
  /** «Come hai pagato?»; `''` until chosen. Payer and method follow from it (decision 145). */
  readonly paymentWay: PaymentWay | '';
  /** «con»: how an administrator's advance was paid, asked only for that way. */
  readonly advanceMethod: AdvanceMethod;
  /** Mileage: kilometres and €/km as typed, and «da → a» (also transport). */
  readonly km: string;
  readonly aciRate: string;
  readonly route: string;
  /** Lodging: the nights, as typed. */
  readonly referenceDays: string;
  readonly businessReason: string;
  readonly place: string;
  readonly peopleInvolved: string;
  readonly description: string;
  readonly taxLines: readonly TaxLineDraft[];
  /** The card chosen in the owner's list, `''` for «Non in elenco»; its label as stored. */
  readonly cardId: string;
  readonly cardLabel: string;
  /** The vehicle chosen in the owner's list, `''` for «Non in elenco»; its label as stored. */
  readonly vehicleId: string;
  readonly vehicleLabel: string;
  /** The supplier picked from the list, `''` while the name is free text. */
  readonly supplierId: string;
}

export const EMPTY_FORM: ExpenseForm = {
  date: '',
  total: '',
  supplierName: '',
  supplierVat: '',
  documentNumber: '',
  category: '',
  documentType: '',
  nonTaxable: '',
  paymentWay: '',
  advanceMethod: 'card',
  km: '',
  aciRate: '',
  route: '',
  referenceDays: '',
  businessReason: '',
  place: '',
  peopleInvolved: '',
  description: '',
  taxLines: [],
  cardId: '',
  cardLabel: '',
  vehicleId: '',
  vehicleLabel: '',
  supplierId: '',
};

let lineCounter = 0;
export function newLineKey(): string {
  lineCounter += 1;
  return `line-${lineCounter}`;
}

export function emptyTaxLine(): TaxLineDraft {
  return {
    key: newLineKey(),
    type: 'vat',
    mode: 'rate',
    rate: '',
    nature: '',
    base: '',
    tax: '',
    rateUnclear: false,
  };
}

/** «48.50» → «48,50»: an API amount as it reads in an Italian field. */
export function amountToField(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  return /^\d+\.\d{2}$/.test(amount) ? amount.replace('.', ',') : amount;
}

/** Cents of an API amount («48.50» → 4850), integer arithmetic only. */
export function toCents(amount: string): number {
  const [whole, fraction] = amount.split('.');
  return Number(whole) * 100 + Number(fraction ?? '0');
}

/** 4850 → «48.50». */
export function fromCents(cents: number): string {
  const abs = Math.abs(cents);
  return `${cents < 0 ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

function draftLineFromApi(line: {
  type: TaxType;
  mode: TaxMode;
  rate?: string;
  nature?: string;
  base?: string;
  tax: string;
  rateUnclear?: boolean;
}): TaxLineDraft {
  return {
    key: newLineKey(),
    type: line.type,
    mode: line.mode,
    rate: line.rate?.replace('.', ',') ?? '',
    nature: line.nature ?? '',
    base: amountToField(line.base),
    tax: amountToField(line.tax),
    rateUnclear: line.rateUnclear ?? false,
  };
}

function documentFields(
  expense: Expense,
): Pick<
  ExpenseForm,
  | 'date'
  | 'total'
  | 'supplierName'
  | 'supplierVat'
  | 'documentNumber'
  | 'category'
  | 'documentType'
  | 'nonTaxable'
> {
  return {
    date: expense.date ?? '',
    total: amountToField(expense.total),
    supplierName: expense.supplierName ?? '',
    supplierVat: expense.supplierVat ?? '',
    documentNumber: expense.documentNumber ?? '',
    category: expense.category ?? '',
    documentType: expense.documentType ?? '',
    nonTaxable: amountToField(expense.nonTaxable),
  };
}

function paymentFields(
  expense: Expense,
): Pick<
  ExpenseForm,
  | 'paymentWay'
  | 'advanceMethod'
  | 'cardId'
  | 'cardLabel'
  | 'vehicleId'
  | 'vehicleLabel'
  | 'supplierId'
> {
  return {
    paymentWay: expense.payer ? paymentWayOf(expense.payer, expense.paymentMethod ?? null) : '',
    advanceMethod: (ADVANCE_METHODS as readonly string[]).includes(expense.paymentMethod ?? '')
      ? (expense.paymentMethod as AdvanceMethod)
      : 'card',
    cardId: expense.cardId ?? '',
    cardLabel: expense.cardLabel ?? '',
    vehicleId: expense.vehicleId ?? '',
    vehicleLabel: expense.vehicleLabel ?? '',
    supplierId: expense.supplierId ?? '',
  };
}

function contextFields(expense: Expense) {
  return {
    km: expense.km?.replace('.', ',') ?? '',
    aciRate: expense.aciRate?.replace('.', ',') ?? '',
    route: expense.route ?? '',
    referenceDays: expense.referenceDays != null ? String(expense.referenceDays) : '',
    businessReason: expense.businessReason ?? '',
    place: expense.place ?? '',
    peopleInvolved: expense.peopleInvolved ?? '',
    description: expense.description ?? '',
  };
}

export function formFromExpense(expense: Expense): ExpenseForm {
  return {
    ...documentFields(expense),
    ...paymentFields(expense),
    ...contextFields(expense),
    taxLines: (expense.taxLines ?? []).map(draftLineFromApi),
  };
}

// ---------------------------------------------------------------------------------------------
// Fields by type (decision 145)

export function formKind(form: ExpenseForm): ExpenseFormKind {
  return expenseFormKind(form.category);
}

/** Whether a field shows for the form's type: the kind's rules hide some. */
export function isShown(form: ExpenseForm, key: string): boolean {
  const kind = formKind(form);
  if (key === 'km' || key === 'aciRate') return kind === 'mileage';
  if (key === 'route') return kind === 'mileage' || kind === 'transport';
  if (key === 'referenceDays') return kind === 'lodging';
  if (key === 'total') return kind !== 'mileage';
  return !EXPENSE_FORM_RULES[kind].hides.includes(key);
}

/** Whether the type requires the field beyond the common ones. */
export function isRequiredByType(form: ExpenseForm, key: string): boolean {
  return EXPENSE_FORM_RULES[formKind(form)].requires.includes(key);
}

/**
 * Which of the owner's payment methods «Come hai pagato?» asks for, if any: a card for a card, an
 * account for a transfer; the company's, the person's own, or either on an advance (decision 153).
 */
export type MethodPick = {
  readonly methodClass: 'card' | 'transfer';
  readonly scope: 'company' | 'personal' | 'any';
};

export function methodPickFor(form: ExpenseForm): MethodPick | null {
  if (formKind(form) === 'mileage') return null;
  switch (form.paymentWay) {
    case 'company-card':
      return { methodClass: 'card', scope: 'company' };
    case 'personal-card':
      return { methodClass: 'card', scope: 'personal' };
    case 'company-transfer':
      return { methodClass: 'transfer', scope: 'company' };
    case 'transfer':
      return { methodClass: 'transfer', scope: 'personal' };
    case 'director-advance':
      return form.advanceMethod === 'card' || form.advanceMethod === 'transfer'
        ? { methodClass: form.advanceMethod, scope: 'any' }
        : null;
    default:
      return null;
  }
}

/** Mileage and fuel ask which of the owner's vehicles. */
export function asksVehicle(form: ExpenseForm): boolean {
  return formKind(form) === 'mileage' || form.category === 'fuel';
}

const decimal = (value: string): string => value.trim().replace(',', '.');

/** A mileage total as an API amount: km × the rate, rounded to the cent; null until both are typed. */
export function mileageTotal(form: ExpenseForm): string | null {
  const cents = mileageTotalCents(decimal(form.km), decimal(form.aciRate));
  return cents === null || cents <= 0 ? null : fromCents(cents);
}

function validNights(value: string): boolean {
  return /^\d+$/.test(value.trim()) && Number(value.trim()) >= 1;
}

function missingMileage(form: ExpenseForm): string[] {
  const missing: string[] = [];
  if (mileageTotal(form) === null) {
    const rate = mileageTotalCents('1000000', decimal(form.aciRate));
    const rateOk = rate !== null && rate > 0;
    if (!rateOk) missing.push('aciRate');
    if (rateOk || parseAmountInput(form.km) === null) missing.push('km');
  }
  return missing;
}

function missingByType(form: ExpenseForm, kind: ExpenseFormKind): string[] {
  const missing: string[] = [];
  for (const key of EXPENSE_FORM_RULES[kind].requires) {
    if (key === 'km' || key === 'aciRate') continue;
    if (key === 'referenceDays') {
      if (!validNights(form.referenceDays)) missing.push(key);
    } else if ((form[key as keyof ExpenseForm] as string).trim() === '') {
      missing.push(key);
    }
  }
  return missing;
}

/** What still stops the save, in the form's order: the field keys, empty when it can be saved. */
export function missingFields(form: ExpenseForm): readonly string[] {
  const kind = formKind(form);
  const missing: string[] = [];
  if (form.category === '') missing.push('category');
  if (form.date === '') missing.push('date');
  if (kind === 'mileage') {
    missing.push(...missingMileage(form));
  } else {
    if (parseAmountInput(form.total) === null) missing.push('total');
    if (form.paymentWay === '') missing.push('paymentWay');
  }
  return [...missing, ...missingByType(form, kind)];
}

// ---------------------------------------------------------------------------------------------
// The totals rule, the same as the server's (design §2.3)

/** A line's base counts when it has one (rate, exempt); its tax when it has one (rate, fixed). */
function lineCents(line: TaxLineDraft): { base: number; tax: number } | null {
  const base = line.mode === 'fixed' ? '0.00' : parseAmountInput(line.base);
  const tax = line.mode === 'exempt' ? '0.00' : parseAmountInput(line.tax);
  if (base === null || tax === null) return null;
  return { base: toCents(base), tax: toCents(tax) };
}

export type TotalsCheck =
  /** No total yet: nothing to say. */
  | { readonly kind: 'no-total' }
  /** An amount in the lines or the non-taxable part is not an amount. */
  | { readonly kind: 'invalid' }
  | { readonly kind: 'ok' }
  | {
      readonly kind: 'mismatch';
      /** |total − sum|, as an API amount. */
      readonly difference: string;
      readonly sum: string;
      readonly total: string;
    };

/**
 * `total = nonTaxable + Σ base (where present) + Σ tax`, to the cent. With no tax lines and the
 * non-taxable part left empty, the whole total is non-taxable (mileage, design §2.3), which is what
 * `toExpenseInput` sends.
 */
export function checkTotals(form: ExpenseForm): TotalsCheck {
  if (formKind(form) === 'mileage') {
    return mileageTotal(form) === null ? { kind: 'no-total' } : { kind: 'ok' };
  }
  const total = parseAmountInput(form.total);
  if (total === null) return { kind: 'no-total' };
  if (form.taxLines.length === 0 && form.nonTaxable.trim() === '') return { kind: 'ok' };
  const nonTaxable = form.nonTaxable.trim() === '' ? '0.00' : parseAmountInput(form.nonTaxable);
  if (nonTaxable === null) return { kind: 'invalid' };
  let sum = toCents(nonTaxable);
  for (const line of form.taxLines) {
    const cents = lineCents(line);
    if (cents === null) return { kind: 'invalid' };
    sum += cents.base + cents.tax;
  }
  const totalCents = toCents(total);
  if (sum === totalCents) return { kind: 'ok' };
  return {
    kind: 'mismatch',
    difference: fromCents(Math.abs(totalCents - sum)),
    sum: fromCents(sum),
    total,
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** The fields whose value depends on the form's type. */
function inputByType(form: ExpenseForm, kind: ExpenseFormKind) {
  const shownRoute = isShown(form, 'route') ? text(form.route) : null;
  return {
    cardId: methodPickFor(form) !== null && form.cardId !== '' ? form.cardId : null,
    vehicleId: asksVehicle(form) && form.vehicleId !== '' ? form.vehicleId : null,
    supplierId:
      kind !== 'mileage' && form.supplierId !== '' && form.supplierName.trim() !== ''
        ? form.supplierId
        : null,
    km: kind === 'mileage' ? decimal(form.km) : null,
    aciRate: kind === 'mileage' ? decimal(form.aciRate) : null,
    route: shownRoute,
    referenceDays:
      kind === 'lodging' && validNights(form.referenceDays) ? Number(form.referenceDays) : null,
  };
}

function mileageInput(form: ExpenseForm, byType: ReturnType<typeof inputByType>): ExpenseInput {
  // Hidden fields are cleared, never kept from another type: no supplier, document or tax.
  const total = mileageTotal(form) ?? undefined;
  return {
    ...(form.date ? { date: form.date } : {}),
    category: form.category,
    ...(total !== undefined ? { total, nonTaxable: total } : {}),
    currency: 'EUR',
    description: form.description.trim(),
    supplierName: null,
    supplierVat: null,
    documentType: null,
    documentNumber: null,
    ...paymentOf('transfer'),
    businessReason: text(form.businessReason),
    peopleInvolved: text(form.peopleInvolved),
    place: text(form.place),
    taxLines: [],
    ...byType,
  };
}

/** The non-taxable part: as typed; empty with no tax lines means the whole total. */
function nonTaxableInput(form: ExpenseForm, total: string | undefined): string | undefined {
  if (form.nonTaxable.trim() !== '') return parseAmountInput(form.nonTaxable) ?? undefined;
  return form.taxLines.length === 0 ? total : '0.00';
}

function taxLinesInput(form: ExpenseForm): NonNullable<ExpenseInput['taxLines']> {
  return form.taxLines.map((line) => ({
    type: line.type,
    mode: line.mode,
    ...(line.mode === 'rate' ? { rate: line.rate.trim().replace(',', '.') } : {}),
    ...(line.mode === 'exempt' ? { nature: line.nature } : {}),
    ...(line.mode !== 'fixed' ? { base: parseAmountInput(line.base) ?? '0.00' } : {}),
    tax: line.mode === 'exempt' ? '0.00' : (parseAmountInput(line.tax) ?? '0.00'),
    rateUnclear: line.rateUnclear,
  }));
}

/** What the request carries. Assumes `checkTotals(form).kind === 'ok'`. */
export function toExpenseInput(form: ExpenseForm): ExpenseInput {
  const kind = formKind(form);
  const byType = inputByType(form, kind);
  if (kind === 'mileage') return mileageInput(form, byType);
  const total = parseAmountInput(form.total) ?? undefined;
  const nonTaxable = nonTaxableInput(form, total);
  return {
    ...(form.date ? { date: form.date } : {}),
    ...(form.category ? { category: form.category } : {}),
    ...(total !== undefined ? { total } : {}),
    ...(nonTaxable !== undefined ? { nonTaxable } : {}),
    currency: 'EUR',
    description: form.description.trim(),
    supplierName: text(form.supplierName),
    supplierVat: text(form.supplierVat),
    documentType: form.documentType === '' ? null : form.documentType,
    documentNumber: text(form.documentNumber),
    ...(form.paymentWay === '' ? {} : paymentOf(form.paymentWay, form.advanceMethod)),
    businessReason: text(form.businessReason),
    peopleInvolved: text(form.peopleInvolved),
    place: text(form.place),
    taxLines: taxLinesInput(form),
    ...byType,
  };
}

// ---------------------------------------------------------------------------------------------
// Proposals from the receipt prefill (design §1.3: ✦, «↺», «✕»)

export const PROPOSABLE = [
  'date',
  'total',
  'supplierName',
  'supplierVat',
  'documentNumber',
  'category',
  'documentType',
  'paymentWay',
  'place',
  'nonTaxable',
  'taxLines',
] as const;
export type ProposableField = (typeof PROPOSABLE)[number];

export interface Proposal {
  /** The value in the form's own shape (field text, or the lines). */
  readonly value: string | readonly TaxLineDraft[];
  readonly uncertain: boolean;
}
export type Proposals = Partial<Record<ProposableField, Proposal>>;

const DOCUMENT_TYPE_SET = new Set<string>(DOCUMENT_TYPES);
/**
 * A payment method read on the receipt, as «Come hai pagato?»: cash and transfer say it all; a card
 * does not say whose, so it is proposed as the person's card and marked to check. Nothing read,
 * nothing proposed (decision 145).
 */
const WAY_BY_READ_METHOD: Readonly<Record<string, { way: PaymentWay; uncertain: boolean }>> = {
  cash: { way: 'cash', uncertain: false },
  transfer: { way: 'transfer', uncertain: false },
  card: { way: 'personal-card', uncertain: true },
};

/** The prefill's fields, in the form's shape. Values the form cannot hold are left out. */
export function proposalsFromPrefill(
  prefill: ReceiptPrefill | null | undefined,
  categories: ReadonlySet<string>,
): Proposals {
  if (!prefill?.readable) return {};
  const f = prefill.fields;
  const out: Proposals = {};
  const put = (
    key: Exclude<ProposableField, 'taxLines'>,
    proposed: { value: string; confidence: string } | null | undefined,
    convert: (value: string) => string | null = (value) => value,
  ): void => {
    if (!proposed) return;
    const value = convert(proposed.value);
    if (value === null || value === '') return;
    out[key] = { value, uncertain: proposed.confidence !== 'high' };
  };
  put('date', f.date, (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null));
  put('total', f.total, amountToField);
  put('supplierName', f.supplierName);
  put('supplierVat', f.supplierVatNumber);
  put('documentNumber', f.documentNumber);
  put('category', f.category, (v) => (categories.has(v) ? v : null));
  put('documentType', f.documentType, (v) => (DOCUMENT_TYPE_SET.has(v) ? v : null));
  const read = f.paymentMethod ? WAY_BY_READ_METHOD[f.paymentMethod.value] : undefined;
  if (f.paymentMethod && read) {
    out.paymentWay = {
      value: read.way,
      uncertain: read.uncertain || f.paymentMethod.confidence !== 'high',
    };
  }
  put('place', f.place);
  put('nonTaxable', f.nonTaxable, amountToField);
  if (f.taxLines && f.taxLines.value.length > 0) {
    out.taxLines = {
      value: f.taxLines.value.map((line) =>
        draftLineFromApi({ ...line, rateUnclear: f.taxLines!.rateUnclear }),
      ),
      uncertain: f.taxLines.confidence !== 'high' || f.taxLines.rateUnclear,
    };
  }
  return out;
}

function sameLines(a: readonly TaxLineDraft[], b: readonly TaxLineDraft[]): boolean {
  const strip = (lines: readonly TaxLineDraft[]) =>
    JSON.stringify(lines.map(({ key: _key, ...rest }) => rest));
  return strip(a) === strip(b);
}

/** Whether the field still holds the proposal: what earns the ✦. */
export function holdsProposal(form: ExpenseForm, key: ProposableField, proposals: Proposals) {
  const proposal = proposals[key];
  if (!proposal) return false;
  if (key === 'taxLines') return sameLines(form.taxLines, proposal.value as TaxLineDraft[]);
  return form[key] === proposal.value;
}

function isEmpty(form: ExpenseForm, key: ProposableField): boolean {
  return key === 'taxLines' ? form.taxLines.length === 0 : form[key] === '';
}

/** Puts a proposal back in its field (the «↺»). */
export function restoreProposal(
  form: ExpenseForm,
  key: ProposableField,
  proposals: Proposals,
): ExpenseForm {
  const proposal = proposals[key];
  if (!proposal) return form;
  if (key === 'taxLines') {
    return {
      ...form,
      taxLines: (proposal.value as TaxLineDraft[]).map((line) => ({ ...line, key: newLineKey() })),
    };
  }
  return { ...form, [key]: proposal.value as string };
}

/**
 * Fills the fields the person has left empty with the proposals; what they already typed while the
 * document was being read stays.
 */
export function applyProposals(form: ExpenseForm, proposals: Proposals): ExpenseForm {
  let next = form;
  for (const key of PROPOSABLE) {
    if (proposals[key] && isEmpty(next, key)) next = restoreProposal(next, key, proposals);
  }
  return next;
}
